import { atom } from 'nanostores';

/**
 * Edit History Store
 *
 * Tracks all visual edits made through Canvas Mode and direct editing.
 * Provides a changelog of style, text, and image changes with revert capability.
 *
 * History is persisted to localStorage per chatId so it survives page refreshes.
 */

export type EditType = 'style' | 'text' | 'image';
export type EditStatus = 'success' | 'failed' | 'reverted';

export interface EditHistoryEntry {
  id: string;
  timestamp: number;
  type: EditType;
  status: EditStatus;
  /** Element tag name (e.g., 'h1', 'div', 'p') */
  tag: string;
  /** Source file path */
  file: string;
  /** Line number in source */
  line: number;
  /** CSS classes BEFORE the edit (for finding the element on revert) */
  classesBefore?: string;
  /** CSS classes AFTER the edit (what's currently in the file) */
  classesAfter?: string;
  /** Human-readable description of the change */
  description: string;
  /** Detailed changes - stores FULL old/new values for revert */
  changes: EditChange[];
  /** Error reason if failed */
  error?: string;
  /** Whether this edit can be reverted */
  canRevert: boolean;
  /** Chat ID for API calls */
  chatId?: string;
}

export interface EditChange {
  property: string;
  oldValue: string;
  newValue: string;
  /** Full old value (not truncated) for revert */
  fullOldValue?: string;
  /** Full new value (not truncated) for revert */
  fullNewValue?: string;
}

/** Maximum number of history entries to keep */
const MAX_HISTORY_ENTRIES = 100;

/** LocalStorage key prefix for edit history */
const STORAGE_KEY_PREFIX = 'dunea-edit-history-';

/** Current chat ID being tracked */
let currentChatId: string | null = null;

/** Flag to prevent saving during initialization */
let isInitializing = false;

/** The edit history store */
export const $editHistory = atom<EditHistoryEntry[]>([]);

// Subscribe to changes and persist to localStorage
$editHistory.subscribe((history) => {
  // Don't save during initialization or if no chatId
  if (isInitializing || !currentChatId || typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + currentChatId, JSON.stringify(history));
    console.log('[EditHistory] Saved to localStorage:', currentChatId, history.length, 'entries');
  } catch (e) {
    console.error('[EditHistory] Failed to save to localStorage:', e);
  }
});

/**
 * Initialize edit history for a specific chat
 * Call this when entering Canvas Mode or loading a chat
 */
export function initEditHistory(chatId: string): void {
  // Prevent the subscription from saving while we're loading
  isInitializing = true;
  currentChatId = chatId;

  if (typeof window === 'undefined') {
    $editHistory.set([]);
    isInitializing = false;

    return;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + chatId);
    console.log('[EditHistory] Loading from localStorage:', chatId, stored ? 'found' : 'not found');

    if (stored) {
      const parsed = JSON.parse(stored) as EditHistoryEntry[];

      // Validate the data structure
      if (Array.isArray(parsed)) {
        $editHistory.set(parsed);
        console.log('[EditHistory] Loaded', parsed.length, 'entries');
        isInitializing = false;

        return;
      }
    }
  } catch (e) {
    console.error('[EditHistory] Failed to load from localStorage:', e);
  }

  $editHistory.set([]);
  isInitializing = false;
}

/**
 * Get the current chat ID being tracked
 */
export function getCurrentChatId(): string | null {
  return currentChatId;
}

/** Generate unique ID */
function generateId(): string {
  return `edit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Format a CSS property name for display */
function formatPropertyName(prop: string): string {
  // Convert kebab-case to Title Case
  return prop
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Truncate text for display */
function truncate(text: string, maxLen: number = 50): string {
  return text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
}

/** Create a human-readable description of the edit */
function createDescription(type: EditType, tag: string, changes: EditChange[]): string {
  if (type === 'text') {
    return `Updated text in <${tag}>`;
  }

  if (type === 'image') {
    return `Replaced image in <${tag}>`;
  }

  // Style changes
  if (changes.length === 1) {
    const change = changes[0];
    return `Changed ${formatPropertyName(change.property).toLowerCase()} on <${tag}>`;
  }

  if (changes.length <= 3) {
    const props = changes.map((c) => formatPropertyName(c.property).toLowerCase());
    return `Changed ${props.join(', ')} on <${tag}>`;
  }

  return `Changed ${changes.length} properties on <${tag}>`;
}

/**
 * Log a successful style edit
 * @param classesBefore - The className before the edit
 * @param classesAfter - The className after the edit (returned from applyStyleEditDirect)
 */
export function logStyleEdit(
  chatId: string,
  tag: string,
  file: string,
  line: number,
  classesBefore: string,
  classesAfter: string,
  deltas: Record<string, { value: string; was: string }>,
): void {
  const changes: EditChange[] = Object.entries(deltas).map(([prop, { value, was }]) => ({
    property: prop,
    oldValue: truncate(was),
    newValue: truncate(value),
    fullOldValue: was,
    fullNewValue: value,
  }));

  if (changes.length === 0) {
    return;
  }

  const entry: EditHistoryEntry = {
    id: generateId(),
    timestamp: Date.now(),
    type: 'style',
    status: 'success',
    tag,
    file,
    line,
    classesBefore,
    classesAfter,
    description: createDescription('style', tag, changes),
    changes,
    canRevert: true,
    chatId,
  };

  addEntry(entry);
}

/**
 * Log a failed style edit
 */
export function logStyleEditFailed(
  tag: string,
  file: string,
  line: number,
  deltas: Record<string, { value: string; was: string }>,
  error: string,
): void {
  const changes: EditChange[] = Object.entries(deltas).map(([prop, { value, was }]) => ({
    property: prop,
    oldValue: truncate(was),
    newValue: truncate(value),
  }));

  const entry: EditHistoryEntry = {
    id: generateId(),
    timestamp: Date.now(),
    type: 'style',
    status: 'failed',
    tag,
    file,
    line,
    description: createDescription('style', tag, changes),
    changes,
    error,
    canRevert: false,
  };

  addEntry(entry);
}

/**
 * Log a successful text edit
 */
export function logTextEdit(
  chatId: string,
  tag: string,
  file: string,
  line: number,
  oldText: string,
  newText: string,
): void {
  const entry: EditHistoryEntry = {
    id: generateId(),
    timestamp: Date.now(),
    type: 'text',
    status: 'success',
    tag,
    file,
    line,
    description: createDescription('text', tag, []),
    changes: [
      {
        property: 'text',
        oldValue: truncate(oldText),
        newValue: truncate(newText),
        fullOldValue: oldText,
        fullNewValue: newText,
      },
    ],
    canRevert: true,
    chatId,
  };

  addEntry(entry);
}

/**
 * Log a failed text edit
 */
export function logTextEditFailed(
  tag: string,
  file: string,
  line: number,
  oldText: string,
  newText: string,
  error: string,
): void {
  const entry: EditHistoryEntry = {
    id: generateId(),
    timestamp: Date.now(),
    type: 'text',
    status: 'failed',
    tag,
    file,
    line,
    description: createDescription('text', tag, []),
    changes: [
      {
        property: 'text',
        oldValue: truncate(oldText),
        newValue: truncate(newText),
      },
    ],
    error,
    canRevert: false,
  };

  addEntry(entry);
}

/**
 * Log a successful image edit
 */
export function logImageEdit(
  chatId: string,
  tag: string,
  file: string,
  line: number,
  oldSrc: string,
  newSrc: string,
): void {
  const entry: EditHistoryEntry = {
    id: generateId(),
    timestamp: Date.now(),
    type: 'image',
    status: 'success',
    tag,
    file,
    line,
    description: createDescription('image', tag, []),
    changes: [
      {
        property: 'src',
        oldValue: truncate(oldSrc, 30),
        newValue: truncate(newSrc, 30),
        fullOldValue: oldSrc,
        fullNewValue: newSrc,
      },
    ],
    canRevert: true,
    chatId,
  };

  addEntry(entry);
}

/**
 * Log a failed image edit
 */
export function logImageEditFailed(
  tag: string,
  file: string,
  line: number,
  oldSrc: string,
  newSrc: string,
  error: string,
): void {
  const entry: EditHistoryEntry = {
    id: generateId(),
    timestamp: Date.now(),
    type: 'image',
    status: 'failed',
    tag,
    file,
    line,
    description: createDescription('image', tag, []),
    changes: [
      {
        property: 'src',
        oldValue: truncate(oldSrc, 30),
        newValue: truncate(newSrc, 30),
      },
    ],
    error,
    canRevert: false,
  };

  addEntry(entry);
}

/**
 * Mark an entry as reverted
 */
export function markAsReverted(entryId: string): void {
  const current = $editHistory.get();
  const updated = current.map((entry) =>
    entry.id === entryId ? { ...entry, status: 'reverted' as EditStatus, canRevert: false } : entry,
  );
  $editHistory.set(updated);
}

/**
 * Log a restore operation - this adds a new entry showing the restore
 * and updates the classesAfter to reflect the new current state
 */
export function logRestoreEdit(
  chatId: string,
  targetEntry: EditHistoryEntry,
  restoredToClasses: string,
  restoredToText?: string,
): void {
  const entry: EditHistoryEntry = {
    id: generateId(),
    timestamp: Date.now(),
    type: targetEntry.type,
    status: 'success',
    tag: targetEntry.tag,
    file: targetEntry.file,
    line: targetEntry.line,
    classesBefore: undefined, // We came from some state
    classesAfter: restoredToClasses, // Now we're at this state
    description: `Restored to earlier version`,
    changes:
      targetEntry.type === 'text' && restoredToText
        ? [
            {
              property: 'text',
              oldValue: '(previous)',
              newValue: truncate(restoredToText),
              fullOldValue: undefined,
              fullNewValue: restoredToText,
            },
          ]
        : [
            {
              property: 'restore',
              oldValue: '(previous)',
              newValue: restoredToClasses ? truncate(restoredToClasses) : '(original)',
            },
          ],
    canRevert: false, // Restore entries can't be reverted
    chatId,
  };

  addEntry(entry);
}

/**
 * Add an entry to the history
 */
function addEntry(entry: EditHistoryEntry): void {
  const current = $editHistory.get();
  const updated = [entry, ...current].slice(0, MAX_HISTORY_ENTRIES);
  $editHistory.set(updated);
}

/**
 * Clear all history for current chat
 */
export function clearEditHistory(): void {
  $editHistory.set([]);

  // Also clear from localStorage
  if (currentChatId && typeof window !== 'undefined') {
    try {
      localStorage.removeItem(STORAGE_KEY_PREFIX + currentChatId);
    } catch (e) {
      console.error('[EditHistory] Failed to clear localStorage:', e);
    }
  }
}

/**
 * Get recent edits (last N entries)
 */
export function getRecentEdits(count: number = 10): EditHistoryEntry[] {
  return $editHistory.get().slice(0, count);
}

/**
 * Get edits for a specific file
 */
export function getEditsForFile(file: string): EditHistoryEntry[] {
  return $editHistory.get().filter((entry) => entry.file === file);
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  if (diff < 60000) {
    return 'Just now';
  }

  if (diff < 3600000) {
    const mins = Math.floor(diff / 60000);
    return `${mins}m ago`;
  }

  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours}h ago`;
  }

  const date = new Date(timestamp);

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
